import { InjectionToken } from '@angular/core';
import { Observable } from 'rxjs';
import { MixedLoyaltyItem } from '../models/mixed-loyalty-item.model';
import { ProcessProviderId } from '../../marketplace/models/process-provider-id';

/**
 * One versionable data source behind `MixedLoyaltyQuery` — marketplace or lotto.
 *
 * `MixedLoyaltyQuery` composes item types but must not know which version of each is in play:
 * subclassing it per combination (as `MixedLoyaltyV2Query` did) needs one subclass for every
 * pairing, so with two independently versioned types you would need four. Each source instead owns
 * its own fetch, selection and — importantly — its own status mapping, since v1 derives status from
 * item flags via `LoyaltyStatusService` while v2 carries a `REWARD_STATUS` that must be mapped.
 *
 * Register implementations with `provideVersionedService` against the tokens below, so
 * `componentVersions.marketplace` and `componentVersions.lotto` select each one independently.
 */
export interface LoyaltySource {
  /** Load this source's items into its store. Completes once the request has been issued. */
  fetch(): Observable<null>;

  /** The favourited items, already mapped to `MixedLoyaltyItem`. */
  selectFavourites(showSoldOut: boolean): Observable<MixedLoyaltyItem[]>;

  /**
   * All items, already mapped to `MixedLoyaltyItem`.
   * @param filterTypes marketplace-only provider filter; ignored by sources it does not apply to.
   */
  selectAll(filterTypes: ProcessProviderId[]): Observable<MixedLoyaltyItem[]>;
}

/** The marketplace source in use, selected by `componentVersions.marketplace`. */
export const MARKETPLACE_SOURCE = new InjectionToken<LoyaltySource>('MarketplaceLoyaltySource');

/** The lotto source in use, selected by `componentVersions.lotto`. */
export const LOTTO_SOURCE = new InjectionToken<LoyaltySource>('LottoLoyaltySource');
